
import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useCustomLabels } from '@/hooks/useCustomLabels';
import { QrCode, Heart, Compass, Info } from 'lucide-react';

interface ExhibitAccessOptionsProps {
  settings?: {
    exhibit_access_buttons: {
      enterNumber: boolean;
      scanQR: boolean;
      favorites: boolean;
      browseTours: boolean;
      visitorInfo?: boolean;
    };
  };
}

// Keypad icon component
const KeypadIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-6 w-6 text-museum-shapes-red"
  >
    <rect x="4" y="4" width="4" height="4" rx="1" />
    <rect x="10" y="4" width="4" height="4" rx="1" />
    <rect x="16" y="4" width="4" height="4" rx="1" />
    <rect x="4" y="10" width="4" height="4" rx="1" />
    <rect x="10" y="10" width="4" height="4" rx="1" />
    <rect x="16" y="10" width="4" height="4" rx="1" />
    <rect x="4" y="16" width="4" height="4" rx="1" />
    <rect x="10" y="16" width="4" height="4" rx="1" />
    <rect x="16" y="16" width="4" height="4" rx="1" />
  </svg>
);

const ExhibitAccessOptions = ({ settings }: ExhibitAccessOptionsProps) => {
  const { t } = useLanguage();
  const { getLabel } = useCustomLabels();
  
  // Use settings from database with proper defaults
  const buttonSettings = settings?.exhibit_access_buttons || {
    enterNumber: true,
    scanQR: true,
    favorites: true,
    browseTours: true,
    visitorInfo: false
  };

  const buttons = [];

  if (buttonSettings.enterNumber) {
    buttons.push(
      <Link 
        key="enter-number"
        to="/exhibit-access" 
        className="museum-card p-6 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1"
      >
        <div className="bg-museum-shapes-red/20 p-3 rounded-full mb-3 group-hover:bg-museum-shapes-red/30 transition-colors">
          <KeypadIcon />
        </div>
        <span className="font-display font-bold">{getLabel('enter_number', 'enter_number')}</span>
      </Link>
    );
  }

  if (buttonSettings.scanQR) {
    buttons.push(
      <Link 
        key="scan-qr"
        to="/exhibit-access?mode=qr" 
        className="museum-card p-6 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1"
      >
        <div className="bg-museum-shapes-blue/20 p-3 rounded-full mb-3 group-hover:bg-museum-shapes-blue/30 transition-colors">
          <QrCode className="h-6 w-6 text-museum-shapes-blue" />
        </div>
        <span className="font-display font-bold">{getLabel('scan_qr', 'scan_qr')}</span>
      </Link>
    );
  }

  if (buttonSettings.favorites) {
    buttons.push(
      <Link 
        key="favorites"
        to="/favorites" 
        className="museum-card p-6 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1"
      >
        <div className="bg-museum-shapes-purple/20 p-3 rounded-full mb-3 group-hover:bg-museum-shapes-purple/30 transition-colors">
          <Heart className="h-6 w-6 text-museum-shapes-purple" />
        </div>
        <span className="font-display font-bold">{getLabel('favorites', 'favorites')}</span>
      </Link>
    );
  }

  if (buttonSettings.browseTours) {
    buttons.push(
      <Link 
        key="browse-tours"
        to="/tours" 
        className="museum-card p-6 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1"
      >
        <div className="bg-museum-shapes-green/20 p-3 rounded-full mb-3 group-hover:bg-museum-shapes-green/30 transition-colors">
          <Compass className="h-6 w-6 text-museum-shapes-green" />
        </div>
        <span className="font-display font-bold">{getLabel('browse_tours', 'browse_tours')}</span>
      </Link>
    );
  }

  if (buttonSettings.visitorInfo) {
    buttons.push(
      <Link 
        key="visitor-info"
        to="/visitor-info" 
        className="museum-card p-6 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1"
      >
        <div className="bg-museum-shapes-orange/20 p-3 rounded-full mb-3 group-hover:bg-museum-shapes-orange/30 transition-colors">
          <Info className="h-6 w-6 text-museum-shapes-orange" />
        </div>
        <span className="font-display font-bold">{getLabel('visitor_info_title', 'visitor_info')}</span>
      </Link>
    );
  }

  if (buttons.length === 0) {
    return null;
  }

  const gridCols = buttons.length === 1 ? 'grid-cols-1' :
                   buttons.length === 2 ? 'grid-cols-2' :
                   buttons.length === 3 ? 'grid-cols-3' :
                   buttons.length === 4 ? 'grid-cols-2 sm:grid-cols-4' :
                   'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5';
  
  return (
    <section className="mb-6 relative z-10">
      <div className={`grid ${gridCols} gap-4`}>
        {buttons}
      </div>
    </section>
  );
};

export default ExhibitAccessOptions;
